import { differenceInCalendarDays, format, parseISO } from 'date-fns'

import { EMPTY_LIST } from '../../../../../constants/common'
import { DATE_SHORT_FORMAT } from '../../../../../constants/date'
import { RENTAL_PORTAL_LISTING_STATUSES } from '../../../../../constants/rentalPortal'
import { toUkTimezoneDate } from '../../../../../utils/date'
import { PhaseItemStatus } from '../../../../Entity'
import {
  getIsListingClosed,
  getIsListingDeactivated,
} from '../../../../RentalPortalListing'
import { parseListingDayInAppTimezone } from '../../../RentalPortal.utils'
import { PHASE_STATE } from '../../../RentalPortalPhaseBar'

import {
  LISTING_AVAILABLE_FROM_SOON_DAYS,
  LISTING_AVAILABLE_FROM_TEXT_COLOR,
  LISTING_PHASE_TERMINAL_STATEMENT_KEY,
  LISTING_PROFILE_INFO_ITEM_ACTION_KEY,
  QA_NAME_LISTING_PROFILE_INFO_RESPONSIBLE_MANAGER_ADD,
  QA_NAME_LISTING_PROFILE_INFO_RESPONSIBLE_MANAGER_EDIT,
} from './RentalPortalListingProfileHeaderInfoItems.constants'

function getListingPhaseItemStatus(state) {
  switch (state) {
    case PHASE_STATE.done:
      return PhaseItemStatus.finished
    case PHASE_STATE.active:
      return PhaseItemStatus.inProgress
    default:
      return PhaseItemStatus.notStarted
  }
}

function getListingTimestampShortDate({ timezone, value }) {
  return format(toUkTimezoneDate(value, timezone), DATE_SHORT_FORMAT)
}

export function getListingShortDate(value) {
  return value ? format(parseISO(value), DATE_SHORT_FORMAT) : null
}

export function getListingPhaseItems({ phases, status, t }) {
  return (phases || EMPTY_LIST).map(({ key, state }) => ({
    id: key,
    name: RENTAL_PORTAL_LISTING_STATUSES[key]
      ? t(RENTAL_PORTAL_LISTING_STATUSES[key].text)
      : null,
    status: getIsListingDeactivated(status)
      ? PhaseItemStatus.notStarted
      : getListingPhaseItemStatus(state),
  }))
}

// TODO: no deactivation timestamp is served, so a deactivated listing is dated by its last update
export function getListingPhaseTerminalDate(listing) {
  return listing?.closed_at ?? listing?.updated
}

export function getListingPhaseTerminalStatement({
  phase,
  t,
  terminalDate,
  timezone,
}) {
  const statementKey = LISTING_PHASE_TERMINAL_STATEMENT_KEY[phase?.key]

  return statementKey && terminalDate
    ? t(statementKey, {
        date: getListingTimestampShortDate({ timezone, value: terminalDate }),
      })
    : t('RentalPortal.ListingPhaseTerminalNoActionKey')
}

export function isListingInactive(listingData) {
  return Boolean(
    listingData?.archived ||
    getIsListingClosed(listingData?.status) ||
    getIsListingDeactivated(listingData?.status)
  )
}

export function getListingAvailableFromTextColor({
  isInactive,
  timezone,
  value,
}) {
  if (value) {
    if (isInactive) {
      return LISTING_AVAILABLE_FROM_TEXT_COLOR.inactive
    }

    const daysLeft = differenceInCalendarDays(
      parseListingDayInAppTimezone(value, timezone),
      toUkTimezoneDate(new Date(), timezone)
    )

    if (daysLeft < 0) {
      return LISTING_AVAILABLE_FROM_TEXT_COLOR.past
    }

    return daysLeft <= LISTING_AVAILABLE_FROM_SOON_DAYS
      ? LISTING_AVAILABLE_FROM_TEXT_COLOR.soon
      : LISTING_AVAILABLE_FROM_TEXT_COLOR.future
  }
}

export function getCanEditListingResponsibleManager({
  isPreview,
  listingData,
}) {
  return Boolean(
    !isPreview &&
    !listingData?.archived &&
    listingData?.permissions?.can_edit_responsible_user
  )
}

export function getCanEditListingAvailableFrom({ isPreview, listingData }) {
  return Boolean(
    !isPreview && !listingData?.archived && listingData?.permissions?.can_edit
  )
}

export function getListingProfileHeaderInfoItemActions(onClick) {
  return {
    [LISTING_PROFILE_INFO_ITEM_ACTION_KEY.add]: { onClick },
    [LISTING_PROFILE_INFO_ITEM_ACTION_KEY.edit]: { onClick },
  }
}

export function getListingProfileManagerItemProps({
  actionButtonClassName,
  actionsWrapperClassName,
  canEdit,
}) {
  return {
    actions: canEdit ? undefined : null,
    canEdit,
    itemActionsProps: canEdit
      ? {
          actionsWrapperClassName,
          buttonsProps: {
            [LISTING_PROFILE_INFO_ITEM_ACTION_KEY.add]: {
              className: actionButtonClassName,
              qaName: QA_NAME_LISTING_PROFILE_INFO_RESPONSIBLE_MANAGER_ADD,
            },
            [LISTING_PROFILE_INFO_ITEM_ACTION_KEY.edit]: {
              className: actionButtonClassName,
              qaName: QA_NAME_LISTING_PROFILE_INFO_RESPONSIBLE_MANAGER_EDIT,
            },
          },
        }
      : undefined,
  }
}
